import { db } from "@lyder-talk/core/lib/db-client";
import { ASSISTANT_CONVERSATION_KIND } from "@/lib/ai-chat/types";
import type { Database } from "@/integrations/supabase/types";

/**
 * Widget history, on the shared `ai_conversations` store.
 *
 * The Lyder assistant widget used to own `ai_assistant_conversations` /
 * `ai_assistant_messages`. It now writes to the same tables Aurora does, tagged
 * `kind = 'assistant'` so the two histories stay separate lists inside one
 * store. This module is the widget's read path; writes go through
 * `@/lib/ai-chat/client` (`createAIConversation`, `patchAIConversation`,
 * `deleteAIConversation`), which already knows the replace-messages contract.
 */

type AIMessageRow = Database["public"]["Tables"]["ai_conversation_messages"]["Row"];
type AIConversationRow = Database["public"]["Tables"]["ai_conversations"]["Row"];

/** Only the message columns the widget reads. */
type AIMessagePick = Pick<AIMessageRow, "id" | "role" | "content" | "created_at">;

/** One turn in the widget transcript. `timestamp` stays epoch-ms for the UI. */
export interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
}

/** A widget conversation, with its messages, as the widget's store holds it. */
export interface ConversationSession {
  id: string;
  title: string;
  messages: Message[];
  createdAt: number;
  updatedAt: number;
}

/** How many conversations the widget history popover will load. */
const MAX_SESSIONS = 50;

function mapRow(row: AIMessagePick): Message {
  return {
    id: row.id,
    // The widget only ever writes user/assistant turns; a row from another
    // role cannot appear under kind='assistant', but narrow defensively.
    role: row.role === "assistant" ? "assistant" : "user",
    content: row.content,
    timestamp: new Date(row.created_at).getTime(),
  };
}

function mapConversation(
  row: Pick<AIConversationRow, "id" | "title" | "created_at" | "updated_at">,
  messages: Message[],
): ConversationSession {
  return {
    id: row.id,
    title: row.title,
    messages,
    createdAt: new Date(row.created_at).getTime(),
    updatedAt: new Date(row.updated_at).getTime(),
  };
}

/**
 * All widget conversations for a user, newest first, each with its messages.
 *
 * Scoped to `kind = 'assistant'`: without the discriminator this would list
 * every Aurora conversation too, and the widget's history popover would show
 * topic-building chats.
 */
export async function loadSessions(userId: string): Promise<ConversationSession[]> {
  try {
    const { data: conversations, error: convError } = await db.from("ai_conversations")
      .select("id, title, created_at, updated_at")
      .eq("user_id", userId)
      .eq("kind", ASSISTANT_CONVERSATION_KIND)
      .is("deleted_at", null)
      .order("updated_at", { ascending: false })
      .limit(MAX_SESSIONS);

    if (convError) {
      console.error("[AI Storage] Error loading conversations:", convError);
      return [];
    }
    if (!conversations || conversations.length === 0) {
      return [];
    }

    const conversationIds = conversations.map((c) => c.id);
    const { data: messages, error: msgError } = await db.from("ai_conversation_messages")
      .select("id, conversation_id, role, content, created_at")
      .in("conversation_id", conversationIds)
      .order("created_at", { ascending: true });

    if (msgError) {
      console.error("[AI Storage] Error loading messages:", msgError);
      return [];
    }

    const messagesByConversation = new Map<string, Message[]>();
    for (const row of messages ?? []) {
      const list = messagesByConversation.get(row.conversation_id) ?? [];
      list.push(mapRow(row));
      messagesByConversation.set(row.conversation_id, list);
    }

    return conversations.map((conv) =>
      mapConversation(conv, messagesByConversation.get(conv.id) ?? []),
    );
  } catch (error) {
    console.error("[AI Storage] Unexpected error loading sessions:", error);
    return [];
  }
}
